<template>
    <div class="main-layout__container">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку пользователей'"
                    :to="router.resolve({ name: 'HandbookUsers' }).href"
                />
            </div>

            <div class="content-header__title">Редактирование пользователя</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <UILoader v-if="isUserLoading" class="editable" />

        <template v-else>
            <div class="content__body">
                <div class="form__content">
                    <div class="form__fields">
                        <div class="form__field-group">
                            <UISelect
                                id="dealer_id"
                                v-model="form.dealer_id"
                                :options="dealerOptions"
                                label="Дилер"
                                :error-message="errors?.dealer_id?.join() ?? ''"
                                searchable
                            />

                            <UIInput
                                id="email"
                                v-model="form.email"
                                label="Email*"
                                :error-message="errors?.email?.join() ?? ''"
                                disabled
                            />

                            <UIInput
                                id="phone"
                                v-model="form.phone"
                                label="Телефон"
                                :error-message="errors?.phone?.join() ?? ''"
                            />
                        </div>

                        <div class="form__field-group">
                            <UIInput
                                id="first_name"
                                v-model="form.first_name"
                                label="Имя*"
                                :error-message="errors?.first_name?.join() ?? ''"
                            />

                            <UIInput
                                id="last_name"
                                v-model="form.last_name"
                                label="Фамилия*"
                                :error-message="errors?.last_name?.join() ?? ''"
                            />

                            <UIInput
                                id="second_name"
                                v-model="form.second_name"
                                label="Отчество"
                                :error-message="errors?.second_name?.join() ?? ''"
                            />
                        </div>

                        <div class="form__field-group">
                            <UIInput
                                id="position"
                                v-model="form.position"
                                label="Должность"
                                :error-message="errors?.position?.join() ?? ''"
                            />

                            <UIInput
                                id="password"
                                v-model="form.password"
                                label="Пароль*"
                                :error-message="errors?.password?.join() ?? ''"
                            />

                            <UIInput
                                id="password_confirmation"
                                v-model="form.password_confirmation"
                                label="Подтверждение пароля*"
                                :error-message="errors?.password?.join() ?? ''"
                            />

                            <UISelect
                                id="is_admin"
                                v-model="form.is_admin"
                                :options="roleOptions"
                                label="Роль*"
                                :error-message="errors?.is_admin?.join() ?? ''"
                            />
                        </div>
                    </div>

                    <div class="form__fields">
                        <div class="form__subtitle">Активность в системе</div>

                        <UIToggle
                            v-if="canChangeActivityStatusUser(user)"
                            v-model="formStatus"
                            label="Активен"
                            :error-message="errors?.status?.join() ?? ''"
                        />

                        <template v-if="canApproveUser(user) || canRejectUser(user)">
                            <div class="form__description">
                                <template v-if="user.status === UserStatus.Moderation">
                                    Пользователь не активен и находится на модерации. Активировать его учетную запись?
                                </template>
                                <template v-if="user.status === UserStatus.ModerationRejected">
                                    Пользователь не активен, потому что был отклонен с модерации. Для его активации нажмите на кнопку ниже
                                </template>
                            </div>

                            <div class="form__field-group">
                                <UISimpleButton
                                    v-if="canApproveUser(user)"
                                    button-type="outlined"
                                    @clicked="handleApproveUser"
                                >
                                    <template #icon>
                                        <img v-svg-inline src="@/assets/icons/ui/check.svg" />
                                    </template>

                                    Подтвердить
                                </UISimpleButton>

                                <UISimpleButton
                                    v-if="canRejectUser(user)"
                                    button-type="danger"
                                    @clicked="handleRejectUser"
                                >
                                    <template #icon>
                                        <img v-svg-inline src="@/assets/icons/ui/mingcute_close-circle-line.svg" />
                                    </template>

                                    Отклонить
                                </UISimpleButton>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <div class="content__footer">
                <div class="form__actions">
                    <UISimpleButton
                        button-type="outlined"
                        @clicked="goToUsersPage"
                    >
                        Выйти без сохранения
                    </UISimpleButton>

                    <UISimpleButton
                        :loading="saveLoading"
                        @clicked="saveUser"
                    >
                        Сохранить
                    </UISimpleButton>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { computed, h, onMounted, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { ModalPluginType } from '@/plugins/modalPlugin';
    import { ModalKey } from '@/injection/keys.ts';

    import { SelectOption } from '@/types/form.type.ts';
    import { DealerListItem } from '@/types/dealer.type.ts';
    import {
        User,
        UserStatus,
        UserUpdateForm,
        UserUpdateFormErrors,
    } from '@/types/user.type.ts';

    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUserRepository from '@/repositories/user/useUserRepository.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';
    import UIToggle from '@/components/ui/UIToggle/UIToggle.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import UserConfirmApprove from '@/components/users/UserConfirmApprove/UserConfirmApprove.vue';
    import UserConfirmReject from '@/components/users/UserConfirmReject/UserConfirmReject.vue';

    const $modal = injectStrict<ModalPluginType>(ModalKey);

    const router = useRouter();
    const route = useRoute();
    const { getUser, updateUser, approveUser, rejectUser } = useUserRepository();
    const { getAllActiveDealers } = useDealerRepository();
    const { canChangeActivityStatusUser, canApproveUser, canRejectUser } = usePermission();
    const { isLoading: isUserLoading, setLoading: setUserLoading } = useLoading();
    const { showSuccess } = useNotifications();

    const user = ref<User>();
    const form = ref<UserUpdateForm>({
        dealer_id: undefined,
        email: '',
        last_name: '',
        first_name: '',
        second_name: '',
        password: '',
        password_confirmation: '',
        status: undefined,
        is_admin: 0,
        position: '',
        phone: '',
    });

    const dealerOptions = ref<SelectOption[]>([]);
    const roleOptions = ref<SelectOption[]>([
        { label: 'Администратор', value: 1 },
        { label: 'Пользователь', value: 0 },
    ]);

    const saveLoading = ref<boolean>(false);
    const errors = ref<UserUpdateFormErrors | null>(null);

    const formStatus = computed<boolean>({
        get: () => form.value.status === UserStatus.Active,
        set: (newIsActive) => {
            form.value.status = newIsActive ? UserStatus.Active : UserStatus.Deactivated;
        },
    });

    const userId = route.params.user_id as unknown as number;

    function saveUser(): void {
        saveLoading.value = true;
        updateUser(userId, form.value)
            .then(() => {
                showSuccess({
                    title: 'Изменения в пользователе сохранены',
                });

                goToUsersPage();
            })
            .catch((updateErrors: UserUpdateFormErrors) => {
                errors.value = updateErrors;
            })
            .finally(() => {
                saveLoading.value = false;
            });
    }

    function handleApproveUser(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(UserConfirmApprove, { user: user.value }),
            },
            props: {
                onConfirm: async () => {
                    await approveUser(userId);

                    showSuccess({
                        title: 'Пользователь подтвержден',
                    });

                    user.value!.status = UserStatus.Active;
                    form.value.status = UserStatus.Active;
                },
            },
        });
    }

    function handleRejectUser(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(UserConfirmReject, { user: user.value }),
            },
            props: {
                onConfirm: async () => {
                    await rejectUser(userId);

                    showSuccess({
                        title: 'Пользователь отклонен',
                    });

                    user.value!.status = UserStatus.ModerationRejected;
                    form.value.status = UserStatus.ModerationRejected;
                },
            },
        });
    }

    function goToUsersPage(): void {
        router.push({ name: 'HandbookUsers' });
    }

    function fetchUser(): void {
        setUserLoading(true);

        getUser(userId).then((fetchedUser: User) => {
            user.value = fetchedUser;

            form.value = {
                dealer_id: fetchedUser.dealer_id ?? undefined,
                email: fetchedUser.email,
                last_name: fetchedUser.last_name ?? '',
                first_name: fetchedUser.first_name ?? '',
                second_name: fetchedUser.second_name ?? '',
                password: '',
                password_confirmation: '',
                status: fetchedUser.status,
                is_admin: fetchedUser.is_admin ? 1 : 0,
                position: fetchedUser.position ?? '',
                phone: fetchedUser.phone ?? '',
            };

            setUserLoading(false);
        });
    }

    function fetchFormOptions(): void {
        getAllActiveDealers().then((dealers: DealerListItem[]) => {
            const options: SelectOption[] = [];
            dealers.forEach((dealer: DealerListItem) => {
                options.push({
                    label: dealer.name,
                    value: dealer.id,
                });
            });

            dealerOptions.value = options;
        });
    }

    onMounted(() => {
        fetchUser();
        fetchFormOptions();
    });
</script>
